import { UpdateActionInterface } from '@omni/blocks';
import { useBlockPageContext } from '@omni/blocks/BlockPageContext';
import { useShellContext } from '@omni/kit';
import { KitButton, KitModal, KitSnack, KitText } from '@omni/kit/components';
import useShellAuthProviders from '@omni/kit/contexts/ShellContext/hooks/useShellAuthProviders';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import BaseService from '@omni/kit/services/BaseService';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import React, { useMemo } from 'react';
import { Platform, View } from 'react-native';

export interface RemovePhoneLoginModalProps {
  appKey: string;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  /** The action to perform when a user delete their phone number. */
  updatePhoneNumberAction?: UpdateActionInterface;
}

const RemovePhoneLoginModal: React.FC<RemovePhoneLoginModalProps> = ({
  appKey,
  visible,
  setVisible,
  updatePhoneNumberAction,
}): JSX.Element => {
  const { tokens } = useShellContext();
  const { targetProviders } = useShellAuthProviders();
  const { authProviderId } = useBlockPageContext();

  const targetProviderToken = useMemo(
    () =>
      authProviderId
        ? tokens.accessTokens[authProviderId] || undefined
        : undefined,
    [authProviderId, tokens.accessTokens]
  );

  const { t } = useTranslation(['account']);

  // ---------------------------------------------------------------------------
  // Global State

  const handleDeletePhoneNumber = async (): Promise<void> => {
    if (updatePhoneNumberAction?.unlinkPhoneUrl) {
      const url = updatePhoneNumberAction.unlinkPhoneUrl;

      const res = await BaseService.Post({
        url: url as string,
        appKey,
        authProviderId,
        targetProviders,
        token: targetProviderToken,
        data: {},
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const ok = res.status === 204 || res.status === 200;

      if (ok) {
        setVisible(false);
        KitSnack.show(t('account:phoneNumberRemoved'), KitSnack.duration.SHORT);
      } else {
        setVisible(false);
        KitSnack.show(t('account:errorMessage'), KitSnack.duration.LONG);
      }
    }
  };

  return (
    <KitModal visible={visible} setVisible={setVisible}>
      <View style={{ margin: Spacing.l }}>
        <KitText
          h1
          testID='RemovePhoneLoginModalTitle'
          style={{ marginBottom: Spacing.xl }}
        >
          {t('account:removePhoneLoginModalTitle')}
        </KitText>
        {Platform.OS === 'web' ? (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-end',
              marginBottom: Spacing.m,
            }}
          >
            <KitButton
              secondary
              title={t('account:removePhoneLoginCancelButton')}
              onPress={() => setVisible(false)}
              style={{ marginRight: Spacing.m }}
            />
            <KitButton
              color={Colors.R500}
              title={t('account:removePhoneLoginButton')}
              onPress={handleDeletePhoneNumber}
            />
          </View>
        ) : (
          <>
            <KitButton
              color={Colors.R500}
              title={t('account:removePhoneLoginButton')}
              onPress={handleDeletePhoneNumber}
              style={{ marginBottom: Spacing.l }}
            />
            <KitButton
              secondary
              title={t('account:removePhoneLoginCancelButton')}
              onPress={() => setVisible(false)}
              style={{ marginBottom: Spacing.xl }}
            />
          </>
        )}
      </View>
    </KitModal>
  );
};

export default RemovePhoneLoginModal;
